<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLocale } from 'shared/composables/useLocale';
import { dateRanges } from '../helpers/DatePickerHelper';
import { isSameYear, isValid } from 'date-fns';

const props = defineProps({
  selectedStartDate: Date,
  selectedEndDate: Date,
  selectedRange: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['open']);
const { t } = useI18n();
const { resolvedLocale } = useLocale();

const formatDate = (date, includeYear = false) =>
  new Intl.DateTimeFormat(resolvedLocale.value, {
    month: 'short',
    day: 'numeric',
    ...(includeYear ? { year: 'numeric' } : {}),
  }).format(date);

const formatDateRange = computed(() => {
  const startDate = props.selectedStartDate;
  const endDate = props.selectedEndDate;

  if (!isValid(startDate) || !isValid(endDate)) {
    return t('DATE_PICKER.PLACEHOLDER');
  }

  if (isSameYear(startDate, new Date()) && isSameYear(endDate, new Date())) {
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  }

  return `${formatDate(startDate, true)} - ${formatDate(endDate, true)}`;
});

const activeDateRange = computed(
  () => dateRanges.find(range => range.value === props.selectedRange).label
);

const openDatePicker = () => {
  emit('open');
};
</script>

<template>
  <button
    class="inline-flex relative items-center rounded-lg gap-2 py-1.5 px-3 h-8 bg-n-alpha-2 hover:bg-n-alpha-1 active:bg-n-alpha-1"
    @click="openDatePicker"
  >
    <fluent-icon class="text-n-slate-12" icon="calendar" size="16" />
    <span class="text-sm font-medium text-n-slate-12">
      {{ $t(activeDateRange) }}
    </span>
    <span class="text-sm font-medium text-n-slate-11">
      {{ formatDateRange }}
    </span>
    <fluent-icon class="text-n-slate-12" icon="chevron-down" size="14" />
  </button>
</template>
